import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faFilter, faDownload, faCalendarAlt, faTable, faSearch } from '@fortawesome/free-solid-svg-icons';

const SensorDataAveragesSuperAdmin = () => {
    const [farms, setFarms] = useState([]);
    const [selectedFarm, setSelectedFarm] = useState('');
    const [ponds, setPonds] = useState([]);
    const [selectedPond, setSelectedPond] = useState('');
    const [sensors, setSensors] = useState([]);
    const [selectedSensors, setSelectedSensors] = useState([]);
    const [sensorData, setSensorData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0,
        currentPage: 1,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
    });
    const { t } = useTranslation();
    const [pageSize] = useState(40);
    const [districts, setDistricts] = useState([]);
    const [ngos, setNgos] = useState([]);
    const [filterNgo, setFilterNgo] = useState('');
    const [filterDistrict, setFilterDistrict] = useState('');
    const [filterSubdistrict, setFilterSubdistrict] = useState('');
    const [filterSubdistricts, setFilterSubdistricts] = useState([]);
    const [aggregation, setAggregation] = useState('day');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Date range - default to current month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const [startDate, setStartDate] = useState(startOfMonth);
    const [endDate, setEndDate] = useState(endOfMonth);

    useEffect(() => {
        fetchFarms();
        fetchDistricts();
        fetchNgos();
    }, []);

    useEffect(() => {
        setSelectedFarm('');
    }, [filterDistrict, filterSubdistrict]);

    const fetchDistricts = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/districts`);
            setDistricts(response.data);
        } catch (err) {
            console.error('Failed to fetch districts', err);
        }
    };

    const fetchNgos = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/ngos`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setNgos(response.data);
        } catch (error) {
            console.error('Failed to fetch NGOs:', error);
        }
    };

    const fetchPonds = async (farmId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/pond/farm/${farmId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setPonds(response.data);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('failed_to_fetch_ponds') || 'Failed to fetch ponds',
                text: error.response?.data?.message || 'An error occurred while fetching ponds.',
            });
        }
    };

    const fetchFarms = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/farm/all/for-admin`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFarms(response.data.farms);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch farms.', 'error');
        }
    };

    const fetchSensors = async (pondId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/sensors/by-pond/${pondId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setSensors(response.data);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('failed_to_fetch_sensors') || 'Failed to fetch sensors',
                text: error.response?.data?.message || 'An error occurred while fetching sensors.',
            });
        }
    };

    const handleFarmSelect = (e) => {
        setSelectedPond('');
        setSensorData([]);
        setPonds([]);
        setSensors([]);
        setSelectedSensors([]);
        const farmId = e.target.value;
        setSelectedFarm(farmId);
        if (farmId) {
            fetchPonds(farmId);
        }
    };

    const handlePondChange = (e) => {
        const pondId = e.target.value;
        setSelectedPond(pondId);
        setSensorData([]);
        setSelectedSensors([]);
        if (pondId) {
            fetchSensors(pondId);
        } else {
            setSensors([]);
            setPagination({
                total: 0,
                currentPage: 1,
                totalPages: 1,
                hasNextPage: false,
                hasPrevPage: false
            });
        }
    };

    const handleSensorChange = (selectedOptions) => {
        setSelectedSensors(selectedOptions || []);
    };

    const fetchSensorData = async (page = 1, loadMore = false) => {
        if (!selectedPond) {
            Swal.fire('Warning', 'Please select a pond first.', 'warning');
            return;
        }

        const token = localStorage.getItem('token');
        setLoading(true);

        let query = `?page=${page}&limit=${pageSize}`;
        if (startDate) query += `&startDate=${startDate.toISOString()}`;
        if (endDate) query += `&endDate=${endDate.toISOString()}`;
        if (selectedSensors.length > 0) {
            const sensorIds = selectedSensors.map(sensor => sensor.value).join(',');
            query += `&sensor_ids=${sensorIds}`;
        }
        query += `&aggregation=${aggregation}`;

        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/pond/${selectedPond}/sensordata-averages${query}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (loadMore) {
                // Stack new data with previous data
                setSensorData(prevData => {
                    const newData = [...prevData];
                    response.data.data.forEach(newSensor => {
                        const existingIndex = newData.findIndex(existing => existing.sensor_name === newSensor.sensor_name);
                        if (existingIndex !== -1) {
                            // Merge data arrays and remove duplicates based on timestamp
                            const existingData = newData[existingIndex].data;
                            const combinedData = [...existingData, ...newSensor.data];
                            const uniqueData = combinedData.filter((item, index, self) => 
                                index === self.findIndex(t => t.timestamp === item.timestamp)
                            );
                            newData[existingIndex] = {
                                ...newSensor,
                                data: uniqueData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                            };
                        } else {
                            newData.push(newSensor);
                        }
                    });
                    return newData;
                });
            } else {
                setSensorData(response.data.data);
            }

            setPagination(response.data.pagination || {
                total: response.data.data.length,
                currentPage: page,
                totalPages: Math.ceil(response.data.data.length / pageSize),
                hasNextPage: false,
                hasPrevPage: false
            });

            setLoading(false);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('failed_to_fetch_sensor_data') || 'Failed to fetch sensor data',
                text: error.response?.data?.message || 'An error occurred while fetching sensor data.',
            });
            setLoading(false);
        }
    };

    const handleLoadMore = () => {
        if (pagination.hasNextPage && !loading) {
            fetchSensorData(pagination.currentPage + 1, true);
        }
    };

    const resetFilters = () => {
        const now = new Date();
        const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

        setStartDate(startOfThisMonth);
        setEndDate(endOfThisMonth);
        setSelectedSensors([]);
        setSensorData([]);
        setPagination({
            total: 0,
            currentPage: 1,
            totalPages: 1,
            hasNextPage: false,
            hasPrevPage: false
        });
        setFilterNgo('');
        setFilterDistrict('');
        setFilterSubdistrict('');
        setSelectedFarm('');
        setSelectedPond('');
        setAggregation('day');
        setSelectedMonth('');
        setSelectedYear(new Date().getFullYear());
    };

    const exportData = () => {
        if (sensorData.length === 0) {
            Swal.fire(t('no_data') || 'No Data', t('no_data_to_export') || 'No data available to export', 'info');
            return;
        }

        const csvRows = [];
        
        // Headers
        csvRows.push(['Sensor Name', 'Timestamp', 'Average Value', 'Min Value', 'Max Value', 'Standard Deviation', 'Count', 'Unit']);
        
        // Data
        sensorData.forEach(sensorInfo => {
            sensorInfo.data.forEach(dataPoint => {
                csvRows.push([
                    sensorInfo.sensor_name,
                    new Date(dataPoint.timestamp).toLocaleString(),
                    dataPoint.avgValue,
                    dataPoint.minValue,
                    dataPoint.maxValue,
                    dataPoint.stdDev,
                    dataPoint.count,
                    sensorInfo.sensor_unit
                ]);
            });
        });

        const csvContent = csvRows.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sensor-averages-${selectedPond}-${new Date().getTime()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Process data for area chart
    const processAreaChartData = (sensor) => {
        if (!sensor || !sensor.data || sensor.data.length === 0) return [];
        
        return sensor.data.map(point => [
            new Date(point.timestamp).getTime(), // x-axis: timestamp in milliseconds
            point.avgValue // y-axis: average value
        ]);
    };

    // Generate area chart options for each sensor
    const getAreaChartOptions = (sensor, index) => {
        const areaData = processAreaChartData(sensor);
        const minData = sensor.data.map(point => [
            new Date(point.timestamp).getTime(),
            point.minValue
        ]);
        const maxData = sensor.data.map(point => [
            new Date(point.timestamp).getTime(),
            point.maxValue
        ]);

        return {
            chart: {
                type: 'area',
                height: 400,
                backgroundColor: 'transparent',
                zoomType: 'x'
            },
            title: {
                text: `${sensor.sensor_name} - Area Chart (${aggregation})`,
                style: {
                    fontSize: '16px',
                    fontWeight: 'bold'
                }
            },
            xAxis: {
                type: 'datetime',
                title: {
                    text: 'Time'
                },
                labels: {
                    format: '{value:%m/%d %H:%M}'
                }
            },
            yAxis: {
                title: {
                    text: `${sensor.sensor_name} (${sensor.sensor_unit})`
                },
                min: Math.min(...sensor.data.map(d => d.minValue)) * 0.95,
                max: Math.max(...sensor.data.map(d => d.maxValue)) * 1.05
            },
            tooltip: {
                shared: true,
                formatter: function () {
                    const pointIndex = sensor.data.findIndex(d => 
                        new Date(d.timestamp).getTime() === this.x
                    );
                    const dataPoint = sensor.data[pointIndex];
                    
                    return `<b>${sensor.sensor_name}</b><br/>
                            Time: ${new Date(this.x).toLocaleString()}<br/>
                            Average: <b>${this.points[0].y.toFixed(3)} ${sensor.sensor_unit}</b><br/>
                            Min: ${dataPoint?.minValue || 'N/A'} ${sensor.sensor_unit}<br/>
                            Max: ${dataPoint?.maxValue || 'N/A'} ${sensor.sensor_unit}<br/>
                            Readings: ${dataPoint?.count || 'N/A'}`;
                }
            },
            legend: {
                enabled: true,
                align: 'center',
                verticalAlign: 'bottom'
            },
            plotOptions: {
                area: {
                    fillOpacity: 0.3,
                    marker: {
                        enabled: true,
                        radius: 3
                    },
                    lineWidth: 2
                }
            },
            series: [
                {
                    name: 'Average',
                    data: areaData,
                    color: '#28a745',
                    fillColor: {
                        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                        stops: [
                            [0, 'rgba(40, 167, 69, 0.4)'],
                            [1, 'rgba(40, 167, 69, 0.1)']
                        ]
                    },
                    marker: {
                        fillColor: '#28a745',
                        lineColor: '#28a745',
                        lineWidth: 1
                    }
                },
                {
                    name: 'Min Values',
                    type: 'line',
                    data: minData,
                    color: '#17a2b8',
                    lineWidth: 1,
                    marker: {
                        enabled: false
                    },
                    enableMouseTracking: false,
                    showInLegend: true
                },
                {
                    name: 'Max Values',
                    type: 'line',
                    data: maxData,
                    color: '#dc3545',
                    lineWidth: 1,
                    marker: {
                        enabled: false
                    },
                    enableMouseTracking: false,
                    showInLegend: true
                }
            ],
            credits: {
                enabled: false
            }
        };
    };

    const calculateTimePeriod = (sensorData, aggregationType) => {
        if (!sensorData || sensorData.length === 0) return { period: 0, unit: 'hours' };
        
        const allTimestamps = sensorData.flatMap(sensor => 
            sensor.data.map(point => new Date(point.timestamp))
        );
        
        if (allTimestamps.length === 0) return { period: 0, unit: 'hours' };
        
        const minTime = new Date(Math.min(...allTimestamps));
        const maxTime = new Date(Math.max(...allTimestamps));
        const diffMs = maxTime - minTime;
        
        if (aggregationType === 'hour') {
            const hours = Math.round(diffMs / (1000 * 60 * 60));
            return { period: Math.max(hours, 1), unit: 'hours' };
        } else {
            const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
            return { period: Math.max(days, 1), unit: 'days' };
        }
    };

    return (
        <div className="container mx-auto p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-blue-600 rounded-full"></div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <FontAwesomeIcon icon={faChartLine} className="text-green-600" />
                        {t('sensor_data_graph') || 'Sensor Data Averages'}
                    </h1>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faFilter} className="text-gray-600" />
                    {t('filters') || 'Filters'}
                </h2>

                {/* Location Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('Filter by District') || 'Filter by District'}
                        </label>
                        <select
                            value={filterDistrict}
                            onChange={(e) => {
                                const selected = e.target.value;
                                setFilterDistrict(selected);
                                const found = districts.find(d => d.name === selected);
                                setFilterSubdistricts(found ? found.subdistricts : []);
                                setFilterSubdistrict('');
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                        >
                            <option value="">{t('All') || 'All'} {t('Districts') || 'Districts'}</option>
                            {districts.map((d) => (
                                <option key={d._id} value={d.name}>{t(d.name)}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('Filter by Subdistrict') || 'Filter by Subdistrict'}
                        </label>
                        <select
                            value={filterSubdistrict}
                            onChange={(e) => setFilterSubdistrict(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                            disabled={!filterDistrict}
                        >
                            <option value="">{t('All') || 'All'} {t('Subdistricts') || 'Subdistricts'}</option>
                            {filterSubdistricts.map((s, i) => (
                                <option key={i} value={s}>{t(s)}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('Filter by NGO') || 'Filter by NGO'}
                        </label>
                        <select
                            value={filterNgo}
                            onChange={(e) => {
                                setFilterNgo(e.target.value);
                                setSelectedFarm('');
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                        >
                            <option value="">{t('All') || 'All'} {t('NGOs') || 'NGOs'}</option>
                            {ngos.map((ngo) => (
                                <option key={ngo._id} value={ngo._id}>{ngo.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Farm and Pond Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label htmlFor="farmSelect" className="block text-gray-700 mb-2 font-medium">
                            <span className="text-red-500">*</span> {t('select_farm') || 'Select Farm'}:
                        </label>
                        <select
                            id="farmSelect"
                            value={selectedFarm}
                            onChange={handleFarmSelect}
                            className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                        >
                            <option value="">-- {t('select_farm') || 'Select Farm'} --</option>
                            {farms
                                .filter((farm) => {
                                    const matchNgo = filterNgo ? farm.ngo_id === filterNgo : true;
                                    const matchDistrict = filterDistrict ? farm.district === filterDistrict : true;
                                    const matchSubdistrict = filterSubdistrict ? farm.subdistrict === filterSubdistrict : true;
                                    return matchNgo && matchDistrict && matchSubdistrict;
                                })
                                .map((farm) => (
                                    <option key={farm._id} value={farm._id}>
                                        🏞️ {farm.name} ({farm.location})
                                    </option>
                                ))
                            }
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            <span className="text-red-500">*</span> {t('select_pond') || 'Select Pond'}
                        </label>
                        <select
                            value={selectedPond}
                            onChange={handlePondChange}
                            className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                        >
                            <option value="">{t('select_pond') || 'Select Pond'}</option>
                            {ponds.map((pond) => (
                                <option key={pond._id} value={pond._id}>
                                    🏞️ {pond.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Sensor Selection, Aggregation, and Month Filter */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('select_sensors') || 'Select Sensors'} <span className="text-sm text-gray-500">({t('optional') || 'optional'})</span>
                        </label>
                        <Select
                            isMulti
                            value={selectedSensors}
                            onChange={handleSensorChange}
                            options={sensors.map(sensor => ({
                                value: sensor._id,
                                label: sensor?.sensor_parameter_id?.sensor_name || sensor.parameter || sensor.parameterName || sensor.type || sensor.name || `Sensor ${sensor._id}`
                            }))}
                            placeholder={selectedPond ? (t('select_sensors') || 'Choose sensors or leave blank for all') : (t('select_pond_first') || 'Select a pond first')}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isDisabled={!selectedPond}
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('aggregation_type') || 'Aggregation Type'}
                        </label>
                        <select
                            value={aggregation}
                            onChange={(e) => setAggregation(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                        >
                            <option value="hour">{t('hourly') || 'Hourly'}</option>
                            <option value="day">{t('daily') || 'Daily'}</option>
                            <option value="week">{t('weekly') || 'Weekly'}</option>
                            <option value="month">{t('monthly') || 'Monthly'}</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('select_month_year') || 'Select Month/Year'} <span className="text-sm text-gray-500">({t('optional') || 'optional'})</span>
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                value={selectedMonth}
                                onChange={(e) => {
                                    const month = e.target.value;
                                    setSelectedMonth(month);
                                    if (month) {
                                        // Auto-set date range when month is selected
                                        const startOfSelectedMonth = new Date(selectedYear, parseInt(month), 1);
                                        const endOfSelectedMonth = new Date(selectedYear, parseInt(month) + 1, 0, 23, 59, 59, 999);
                                        setStartDate(startOfSelectedMonth);
                                        setEndDate(endOfSelectedMonth);
                                    }
                                }}
                                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                            >
                                <option value="">{t('all_months') || 'All Months'}</option>
                                <option value="0">{t('january') || 'January'}</option>
                                <option value="1">{t('february') || 'February'}</option>
                                <option value="2">{t('march') || 'March'}</option>
                                <option value="3">{t('april') || 'April'}</option>
                                <option value="4">{t('may') || 'May'}</option>
                                <option value="5">{t('june') || 'June'}</option>
                                <option value="6">{t('july') || 'July'}</option>
                                <option value="7">{t('august') || 'August'}</option>
                                <option value="8">{t('september') || 'September'}</option>
                                <option value="9">{t('october') || 'October'}</option>
                                <option value="10">{t('november') || 'November'}</option>
                                <option value="11">{t('december') || 'December'}</option>
                            </select>
                            <select
                                value={selectedYear}
                                onChange={(e) => {
                                    const year = parseInt(e.target.value);
                                    setSelectedYear(year);
                                    if (selectedMonth !== '') {
                                        // Update date range when year changes and month is selected
                                        const startOfSelectedMonth = new Date(year, parseInt(selectedMonth), 1);
                                        const endOfSelectedMonth = new Date(year, parseInt(selectedMonth) + 1, 0, 23, 59, 59, 999);
                                        setStartDate(startOfSelectedMonth);
                                        setEndDate(endOfSelectedMonth);
                                    }
                                }}
                                className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400"
                            >
                                {(() => {
                                    const currentYear = new Date().getFullYear();
                                    const years = [];
                                    for (let year = currentYear; year >= currentYear - 5; year--) {
                                        years.push(
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        );
                                    }
                                    return years;
                                })()}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                            {t('start_date') || 'Start Date'}
                        </label>
                        <DatePicker
                            selected={startDate}
                            onChange={setStartDate}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="MM/dd HH:mm"
                            className="w-full px-3 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholderText={t('from_date_time') || 'From date & time'}
                        />
                    </div>
                    
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">
                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                            {t('end_date') || 'End Date'}
                        </label>
                        <DatePicker
                            selected={endDate}
                            onChange={setEndDate}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="MM/dd HH:mm"
                            className="w-full px-3 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                            placeholderText={t('to_date_time') || 'To date & time'}
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                        onClick={() => fetchSensorData(1)}
                        disabled={!selectedPond || loading}
                        className={`
                            flex items-center justify-center py-3 px-6 rounded-lg text-white font-medium
                            transition-all duration-300 ease-in-out
                            ${!selectedPond || loading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 shadow-lg hover:shadow-xl'
                        }
                        `}
                    >
                        <FontAwesomeIcon icon={faSearch} className="mr-2" />
                        {loading ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('loading') || 'Loading...'}
                            </span>
                        ) : (t('load_data') || 'Load Data')}
                    </button>

                    <button
                        onClick={exportData}
                        disabled={!sensorData.length || loading}
                        className={`
                            flex items-center justify-center py-3 px-6 rounded-lg font-medium
                            transition-all duration-300 ease-in-out
                            ${!sensorData.length || loading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-lg hover:shadow-xl'
                        }
                        `}
                    >
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        {t('export_csv') || 'Export CSV'}
                    </button>

                    <button
                        onClick={resetFilters}
                        disabled={loading}
                        className={`
                            flex items-center justify-center py-3 px-6 rounded-lg font-medium
                            transition-all duration-300 ease-in-out
                            ${loading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300 hover:shadow-md'
                        }
                        `}
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        {t('reset') || 'Reset'}
                    </button>

                </div>
            </div>


            {/* Area Charts */}
            {sensorData.length > 0 && (
                <div className="space-y-6">
                    {/* Individual Area Charts for Each Sensor */}
                    {sensorData.map((sensor, index) => (
                        <div key={index} className="bg-white rounded-lg shadow-lg p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faChartLine} className="text-green-600" />
                                    {sensor.sensor_name} - Area Chart ({aggregation})
                                </h3>
                                <div className="text-sm text-gray-500 flex items-center gap-4">
                                    <span>{sensor.data.length} {t('data_points') || 'data points'}</span>
                                    <span className="text-xs text-gray-400">{sensor.sensor_unit}</span>
                                </div>
                                {pagination.hasNextPage && (
                                    <div className="text-center">
                                        <button
                                            onClick={handleLoadMore}
                                            disabled={loading}
                                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors disabled:bg-gray-400"
                                        >
                                            {loading ? t('loading') || 'Loading...' : t('load_more') || 'Load More Data'}
                                        </button>
                                    </div>
                                )}
                            </div>
                            
                            {/* Sensor Statistics */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                                {(() => {
                                    const allValues = sensor.data.map(d => d.avgValue);
                                    const overallAvg = allValues.reduce((sum, val) => sum + val, 0) / allValues.length;
                                    const minValue = Math.min(...sensor.data.map(d => d.minValue));
                                    const maxValue = Math.max(...sensor.data.map(d => d.maxValue));
                                    const totalReadings = sensor.data.reduce((sum, d) => sum + d.count, 0);
                                    const timePeriod = calculateTimePeriod([sensor], aggregation);
                                    
                                    return (
                                        <>
                                            <div className="text-center">
                                                <div className="text-lg font-semibold text-gray-800">{overallAvg.toFixed(3)}</div>
                                                <div className="text-sm text-gray-600">Average</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-lg font-semibold text-green-600">{minValue}</div>
                                                <div className="text-sm text-gray-600">Minimum</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-lg font-semibold text-red-600">{maxValue}</div>
                                                <div className="text-sm text-gray-600">Maximum</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-lg font-semibold text-blue-600">{totalReadings}</div>
                                                <div className="text-sm text-gray-600">Total Readings</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-lg font-semibold text-purple-600">{timePeriod.period} {timePeriod.unit}</div>
                                                <div className="text-sm text-gray-600">Time Period</div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>

                            <div className="h-[450px]">
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={getAreaChartOptions(sensor, index)}
                                />
                            </div>

                        </div>
                    ))}

                    {/* Statistics Table */}
                    <div className="bg-white rounded-lg shadow-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <FontAwesomeIcon icon={faTable} className="text-blue-600" />
                            {t('statistics_summary') || 'Statistics Summary'}
                        </h3>
                        
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('sensor') || 'Sensor'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('overall_avg') || 'Overall Avg'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('min_value') || 'Min'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('max_value') || 'Max'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('total_readings') || 'Total Readings'}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('time_period') || 'Time Period'}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {sensorData.map((sensor, index) => {
                                        const allValues = sensor.data.map(d => d.avgValue);
                                        const overallAvg = allValues.reduce((sum, val) => sum + val, 0) / allValues.length;
                                        const minValue = Math.min(...sensor.data.map(d => d.minValue));
                                        const maxValue = Math.max(...sensor.data.map(d => d.maxValue));
                                        const totalReadings = sensor.data.reduce((sum, d) => sum + d.count, 0);

                                        return (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center">
                                                        <div>
                                                            <div className="text-sm font-medium text-gray-900">{sensor.sensor_name}</div>
                                                            <div className="text-sm text-gray-500">{sensor.sensor_unit}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {overallAvg.toFixed(3)} {sensor.sensor_unit}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {minValue} {sensor.sensor_unit}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {maxValue} {sensor.sensor_unit}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {totalReadings}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {(() => {
                                                        const timePeriod = calculateTimePeriod([sensor], aggregation);
                                                        return `${timePeriod.period} ${timePeriod.unit}`;
                                                    })()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Load More Button */}

                </div>
            )}

            {/* No Data State */}
            {sensorData.length === 0 && !loading && (
                <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                    <FontAwesomeIcon icon={faChartLine} className="text-6xl text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('no_data') || 'No sensor data available'}</h3>
                    <p className="text-gray-500">
                        {selectedPond ? (t('click_load_data_button') || 'Click the "Load Data" button to fetch sensor averages.') : (t('select_pond_to_view_data') || 'Please select a farm and pond to view sensor data.')}
                    </p>
                </div>
            )}
        </div>
    );
};

export default SensorDataAveragesSuperAdmin;